import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import useCache from '../hooks/useCache';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Alert,
  Button,
  Badge,
  Table,
  Spinner,
  Modal,
  Form
} from '@cloudscape-design/components';

const stripePromise = loadStripe(config.StripePublishableKey);

function UpdatePaymentForm({ onClose, onSuccess, clientSecret, customerId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [elementReady, setElementReady] = useState(false);
  const [showExtensionWarning, setShowExtensionWarning] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Detect if PaymentElement fails to become ready (extension interference)
  useEffect(() => {
    if (elementReady) return;
    const timer = setTimeout(() => {
      if (!elementReady) {
        setShowExtensionWarning(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [elementReady]);

  const handleCheckoutFallback = async () => {
    try {
      setCheckoutLoading(true);
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      // Route through /payment-setup which has checkout_session_id handling,
      // then redirect back to /financials after processing
      sessionStorage.setItem('recapify_redirect_after_setup', '/financials');

      const command = new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({
          action: 'create_checkout_session',
          email: session.tokens?.idToken?.payload?.email,
          userPoolSub: session.tokens?.idToken?.payload?.sub,
          successUrl: `${window.location.origin}/payment-setup`,
          cancelUrl: `${window.location.origin}/financials`
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        window.location.href = data.checkoutUrl;
      } else {
        const errorBody = JSON.parse(result.body);
        setError(errorBody.error || 'Failed to create checkout session');
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error('Checkout fallback error:', err);
      setError(err.message || 'Failed to open secure checkout.');
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin,
          payment_method_data: {
            billing_details: {
              name: cardholderName
            }
          }
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      const session = await fetchAuthSession();
      
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({
          action: 'update_payment_method',
          paymentMethodId: setupIntent.payment_method,
          customerId: customerId
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      if (result.statusCode === 200) {
        onSuccess();
      } else {
        throw new Error('Failed to update payment method');
      }
    } catch (err) {
      console.error('Payment update error:', err);
      setError(err.message || 'Payment update failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Form>
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {showExtensionWarning && !elementReady && (
          <Alert type="warning" header="Payment form not loading?">
            A browser extension may be interfering with the secure payment form. 
            You can try disabling password managers or autofill extensions, or use our secure checkout page instead.
            <br /><br />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="primary"
                loading={checkoutLoading}
                onClick={handleCheckoutFallback}
              >
                Use Secure Checkout Instead
              </Button>
            </div>
          </Alert>
        )}
        
        <Box>
          {elementReady && (() => {
            const isDark = (localStorage.getItem('cloudscape-theme') || 'light') === 'dark';
            return (
              <div style={{ marginBottom: '4px' }}>
                <label style={{
                  color: isDark ? '#e0e0e0' : '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'block',
                  marginBottom: '6px'
                }}>Cardholder name</label>
                <input
                  type="text"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="Full name on card"
                  aria-label="Cardholder name"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: isDark ? '#1e1e3f' : '#ffffff',
                    border: isDark ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: isDark ? '#e0e0e0' : '#1f2937',
                    fontSize: '16px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = isDark ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            );
          })()}
          <PaymentElement options={{ fields: { billingDetails: { name: 'never' } } }} onReady={() => setElementReady(true)} />
        </Box>
        
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={isLoading}
              onClick={handleSubmit}
              disabled={!stripe || !clientSecret}
            >
              Update Payment Method
            </Button>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Form>
  );
}

function PaymentInfo({ onCacheReady }) {
  const navigate = useNavigate();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [error, setError] = useState('');

  const fetchPaymentData = async () => {
    const session = await fetchAuthSession();
    
    const lambda = new LambdaClient({
      region: config.Region,
      credentials: session.credentials
    });

    const [paymentResult, invoiceResult] = await Promise.all([
      lambda.send(new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({ action: 'get_payment_info' })
      })),
      lambda.send(new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({ action: 'get_invoices' })
      }))
    ]);

    const paymentParsed = JSON.parse(new TextDecoder().decode(paymentResult.Payload));
    const invoiceParsed = JSON.parse(new TextDecoder().decode(invoiceResult.Payload));

    const paymentInfo = paymentParsed.statusCode === 200 ? JSON.parse(paymentParsed.body) : null;
    const invoices = invoiceParsed.statusCode === 200 ? (JSON.parse(invoiceParsed.body).invoices || []) : [];

    if (!paymentInfo) throw new Error('Failed to fetch payment information');

    return { paymentInfo, invoices };
  };

  const { data, loading: isLoading, error: cacheError, cachedAt, isCached, refresh } = useCache('payment-info', fetchPaymentData);

  useEffect(() => {
    if (onCacheReady) onCacheReady({ cachedAt, isCached, loading: isLoading, refresh });
  }, [cachedAt, isCached, isLoading, refresh, onCacheReady]);

  const paymentInfo = data?.paymentInfo || null;
  const invoices = data?.invoices || [];

  const handleUpdatePayment = async () => {
    try {
      setError('');
      const session = await fetchAuthSession();
      
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({ 
          action: 'create_setup_intent'
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        setClientSecret(data.clientSecret);
        setCustomerId(data.customerId);
        setShowUpdateModal(true);
      } else {
        throw new Error('Failed to initialize payment update');
      }
    } catch (err) {
      console.error('Error initializing payment update:', err);
      setError('Failed to initialize payment update');
    }
  };

  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    setClientSecret('');
    refresh();
  };

  if (isLoading && !data) {
    return (
      <Container>
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      </Container>
    );
  }

  if ((error || cacheError) && !data) {
    return (
      <Container>
        <Alert type="error">{error || cacheError}</Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* Update Payment Modal */}
      {showUpdateModal && clientSecret && (
        <Modal
          visible={showUpdateModal}
          onDismiss={() => setShowUpdateModal(false)}
          header="Update Payment Method"
          size="medium"
        >
          <Elements stripe={stripePromise} options={{ 
            clientSecret,
            appearance: (() => {
              const isDark = (localStorage.getItem('cloudscape-theme') || 'light') === 'dark';
              return {
                theme: 'none',
                variables: {
                  colorPrimary: '#667eea',
                  colorBackground: isDark ? '#1a1a2e' : '#ffffff',
                  colorText: isDark ? '#e0e0e0' : '#1f2937',
                  colorDanger: '#dc2626',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  spacingUnit: '4px',
                  borderRadius: '8px',
                },
                rules: {
                  '.Input': {
                    backgroundColor: isDark ? '#1e1e3f' : '#ffffff',
                    border: isDark ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
                    boxShadow: 'none',
                    padding: '12px',
                    color: isDark ? '#e0e0e0' : '#1f2937',
                  },
                  '.Input:focus': {
                    border: '1px solid #667eea',
                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                  },
                  '.Label': {
                    color: isDark ? '#e0e0e0' : '#374151',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '6px',
                  },
                  '.Error': {
                    color: '#dc2626',
                  },
                }
              };
            })()
          }}>
            <UpdatePaymentForm 
              onClose={() => setShowUpdateModal(false)}
              onSuccess={handleUpdateSuccess}
              clientSecret={clientSecret}
              customerId={customerId}
            />
          </Elements>
        </Modal>
      )}

      {/* Payment Method */}
      <Container>
        <Header variant="h2">Payment Method</Header>
        {paymentInfo?.hasPaymentMethod ? (
          <SpaceBetween size="m">
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Card</Box>
                <Box>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Badge color="blue">{paymentInfo.card.brand.toUpperCase()}</Badge>
                    <span>•••• {paymentInfo.card.last4}</span>
                  </SpaceBetween>
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Expires</Box>
                <Box>{paymentInfo.card.expMonth}/{paymentInfo.card.expYear}</Box>
              </div>
            </ColumnLayout>
            <Box>
              <Button onClick={handleUpdatePayment}>Update Payment Method</Button>
            </Box>
          </SpaceBetween>
        ) : (
          <Alert 
            type="warning"
            action={
              <Button onClick={() => navigate('/payment-setup')}>
                Add Payment Method
              </Button>
            }
          >
            No payment method on file. Add a payment method to continue using services.
          </Alert>
        )}
      </Container>

      {/* Invoices */}
      <Container>
        <Header variant="h2">Billing History</Header>
        {invoices.length > 0 ? (
          <Table
            columnDefinitions={[
              {
                id: 'date',
                header: 'Date',
                cell: item => new Date(item.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              },
              {
                id: 'period',
                header: 'Period',
                cell: item => item.period
              },
              {
                id: 'amount',
                header: 'Amount',
                cell: item => new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(item.amount)
              },
              {
                id: 'status',
                header: 'Status',
                cell: item => (
                  <Badge color={item.status === 'paid' ? 'green' : item.status === 'open' ? 'blue' : 'grey'}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </Badge>
                )
              },
              {
                id: 'invoice',
                header: 'Invoice',
                cell: item => (
                  item.invoiceUrl && (
                    <Button 
                      iconName="download" 
                      variant="normal"
                      href={item.invoiceUrl}
                    >
                      Download
                    </Button>
                  )
                )
              }
            ]}
            items={invoices}
            variant="embedded"
          />
        ) : (
          <Alert type="info">
            No invoices yet. Invoices are generated automatically at the end of each month based on your usage.
          </Alert>
        )}
      </Container>
    </SpaceBetween>
  );
}

export default PaymentInfo;
